import { Copy, KeyRound, Plus, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useTRPC } from '../../hooks/useTRPC';
import { cn } from '../../lib/utils';
import {
  API_KEY_MANAGEMENT_PERMISSION_MESSAGE,
  isManageTeamPermissionError,
} from '../../utils/apiKeyAccess';
import { captureAndReportError } from '../../utils/errorReporting';
import { Button } from '../Button';
import { Input } from '../Input';
import {
  PortalCard,
  SectionLabel,
  Skeleton,
  SKELETON_REVEAL_CLASS,
  SkeletonGroup,
} from '../portal';
import { SettingsSection } from '../settings';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '../ui/dialog';

type PortalTRPCClient = ReturnType<typeof useTRPC> & {
  developerApiKey: {
    listKeys: {
      query: (input: {
        publisherId: string;
        publisherType: 'organization' | 'individual';
      }) => Promise<any>;
    };
    createKey: {
      mutate: (input: {
        publisherId: string;
        publisherType: 'organization' | 'individual';
        label: string;
        permissions: Array<'ManageReleases' | 'SubmitToStore'>;
      }) => Promise<any>;
    };
    revokeKey: {
      mutate: (input: Record<string, unknown>) => Promise<any>;
    };
  };
};

const DEFAULT_API_KEY_PERMISSIONS = [
  'ManageReleases',
  'SubmitToStore',
] as const;

const PERMISSION_LABELS: Record<string, string> = {
  ManageReleases: 'Releases',
  ManageNFTs: 'NFTs',
  SubmitToStore: 'Store',
  ManageTeam: 'Team',
};

type DeveloperApiKey = {
  id: string;
  label: string;
  keyPrefix?: string;
  permissions?: string[];
  created?: string;
  lastUsedAt?: string | null;
  revokedAt?: string | null;
};

export type PublisherScope = {
  publisherId: string;
  publisherType: 'organization' | 'individual';
};

interface ApiKeyManagementPanelProps {
  publisherScope: PublisherScope;
  onPermissionDenied?: () => void;
}

// Rows sit inside a `SettingsSection`, so the card supplies the side padding.
const ROW_GRID =
  'grid grid-cols-[1fr_auto] gap-4 md:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,0.8fr)_auto]';

function asPortalClient(client: ReturnType<typeof useTRPC>): PortalTRPCClient {
  return client as unknown as PortalTRPCClient;
}

function unwrapPortalResult<T>(result: unknown): T {
  if (
    result &&
    typeof result === 'object' &&
    '_tag' in result &&
    (result as { _tag?: string })._tag === 'Left'
  ) {
    const left = result as { left?: { message?: string } };
    throw new Error(left.left?.message || 'Request failed');
  }

  if (
    result &&
    typeof result === 'object' &&
    '_tag' in result &&
    (result as { _tag?: string })._tag === 'Right'
  ) {
    return (result as unknown as { right: T }).right;
  }

  return result as T;
}

function readKeys(result: unknown): DeveloperApiKey[] {
  if (Array.isArray(result)) {
    return result as DeveloperApiKey[];
  }

  if (result && typeof result === 'object') {
    const maybeKeys = (result as { keys?: unknown }).keys;
    if (Array.isArray(maybeKeys)) {
      return maybeKeys as DeveloperApiKey[];
    }
  }

  return [];
}

function readSecret(result: unknown): string | undefined {
  if (!result || typeof result !== 'object') {
    return undefined;
  }

  const secret = (result as { secret?: unknown }).secret;
  if (typeof secret === 'string' && secret.length > 0) {
    return secret;
  }

  const apiKey = (result as { apiKey?: unknown }).apiKey;
  if (typeof apiKey === 'string' && apiKey.length > 0) {
    return apiKey;
  }

  const value = (result as { value?: unknown }).value;
  if (typeof value === 'string' && value.length > 0) {
    return value;
  }

  return undefined;
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffHours < 1) return 'Just now';
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}

function PermissionChips({
  permissions,
  className,
}: {
  permissions: string[];
  className?: string;
}) {
  if (permissions.length === 0) {
    return <span className="text-xs text-seeker-500">—</span>;
  }
  return (
    <div className={cn('flex flex-wrap items-center gap-1.5', className)}>
      {permissions.map((perm) => (
        <span
          key={perm}
          className="rounded-md bg-seeker-800 px-2 py-0.5 text-xs leading-4 text-seeker-300"
        >
          {PERMISSION_LABELS[perm] ?? perm}
        </span>
      ))}
    </div>
  );
}

function KeyRow({
  apiKey,
  onDelete,
}: {
  apiKey: DeveloperApiKey;
  onDelete: (key: DeveloperApiKey) => void;
}) {
  const permissions = apiKey.permissions ?? [];

  return (
    // `motion-enter` runs once per row mount, so a freshly created key rises in
    // while existing rows (stable keys) stay put on refetch.
    <div
      className={cn(
        ROW_GRID,
        'group motion-enter items-start rounded-lg py-4 transition-colors duration-150 hover:bg-seeker-800/40',
      )}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span
            className="truncate text-sm font-medium text-white"
            title={apiKey.label}
          >
            {apiKey.label}
          </span>
          <span
            aria-hidden="true"
            className="inline-block size-1.5 shrink-0 rounded-full bg-green-400"
          />
        </div>
        {apiKey.keyPrefix && (
          <code className="mt-1 block truncate font-mono text-xs text-seeker-400">
            {apiKey.keyPrefix}...
          </code>
        )}
      </div>

      <div className="hidden pt-0.5 md:block">
        <PermissionChips permissions={permissions} />
      </div>

      <div className="hidden flex-col gap-0.5 md:flex">
        {apiKey.created ? (
          <span className="text-xs text-seeker-400">
            Created {formatDate(apiKey.created)}
          </span>
        ) : (
          <span className="text-xs text-seeker-500">—</span>
        )}
        <span className="text-xs text-seeker-500">
          {apiKey.lastUsedAt
            ? `Last used ${formatDate(apiKey.lastUsedAt)}`
            : 'Never used'}
        </span>
      </div>

      <div className="flex items-center justify-end self-center">
        <button
          type="button"
          onClick={() => onDelete(apiKey)}
          className="flex size-8 items-center justify-center rounded-md text-seeker-500 transition-[color,background-color,opacity] duration-150 hover:bg-red-500/10 hover:text-red-400 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 md:opacity-0 md:group-hover:opacity-100"
          aria-label={`Delete ${apiKey.label}`}
        >
          <Trash2 className="size-4" />
        </button>
      </div>

      {/* Mobile-only meta row */}
      <div className="col-span-2 flex flex-wrap items-center gap-2 md:hidden">
        {permissions.length > 0 && (
          <PermissionChips permissions={permissions} />
        )}
        <span className="text-xs text-seeker-500">
          {apiKey.created ? formatDate(apiKey.created) : ''}
          {apiKey.lastUsedAt
            ? ` · Used ${formatDate(apiKey.lastUsedAt)}`
            : apiKey.created
              ? ' · Never used'
              : ''}
        </span>
      </div>
    </div>
  );
}

function SecretBanner({
  secret,
  onDismiss,
}: {
  secret: string;
  onDismiss: () => void;
}) {
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Could not copy');
    }
  };

  return (
    <PortalCard className="motion-enter overflow-hidden">
      <div className="flex flex-col gap-3 px-5 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2.5">
          <KeyRound className="size-4 shrink-0 text-green-400" />
          <p className="text-sm font-medium text-white">
            Save your secret: it won&apos;t be shown again
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outlineMuted"
            className="h-8 px-3"
            onClick={() => void handleCopy()}
          >
            <Copy className="size-3.5" />
            <span className="text-xs">Copy</span>
          </Button>
          <Button variant="ghostMuted" className="h-8 px-3" onClick={onDismiss}>
            <span className="text-xs">Dismiss</span>
          </Button>
        </div>
      </div>
      <div className="border-t border-seeker-800 bg-seeker-950 px-5 py-3">
        <code className="block break-all font-mono text-[13px] leading-relaxed text-white">
          {secret}
        </code>
      </div>
    </PortalCard>
  );
}

/** Mirrors `KeyRow`: 20px label + 16px prefix, permission chips, two 16px activity lines, 32px action slot. */
function KeyRowSkeleton() {
  return (
    <div className={cn(ROW_GRID, 'items-start py-4')} aria-hidden="true">
      <div className="min-w-0">
        <div className="flex h-5 items-center gap-2">
          <Skeleton className="h-3.5 w-32" />
          <Skeleton className="size-1.5" rounded="full" />
        </div>
        <div className="mt-1 flex h-4 items-center">
          <Skeleton className="h-3 w-24" />
        </div>
      </div>
      <div className="hidden pt-0.5 md:flex md:items-center md:gap-1.5">
        <Skeleton className="h-5 w-16" />
        <Skeleton className="h-5 w-12" />
      </div>
      <div className="hidden flex-col gap-0.5 md:flex">
        <div className="flex h-4 items-center">
          <Skeleton className="h-3 w-24" />
        </div>
        <div className="flex h-4 items-center">
          <Skeleton className="h-3 w-20" />
        </div>
      </div>
      <div className="flex items-center justify-end self-center">
        <span className="size-8" />
      </div>
      <div className="col-span-2 flex h-5 items-center gap-2 md:hidden">
        <Skeleton className="h-5 w-16" />
        <Skeleton className="h-3 w-28" />
      </div>
    </div>
  );
}

/** Column labels above the key rows; shared by the list and its skeleton. */
function KeyListHeader() {
  return (
    <div className={cn(ROW_GRID, 'hidden md:grid')}>
      <SectionLabel>Name</SectionLabel>
      <SectionLabel>Permissions</SectionLabel>
      <SectionLabel>Activity</SectionLabel>
      <span className="w-8" />
    </div>
  );
}

/** 20px section-title line, so the header row keeps its loaded height. */
function TitleSkeleton({ className }: { className?: string }) {
  return (
    <span className="flex h-5 items-center">
      <Skeleton className={cn('h-3.5 w-28', className)} />
    </span>
  );
}

/** Mirrors both sections below (create row + key list) while access loads. */
export function ApiKeysSkeleton() {
  return (
    <SkeletonGroup label="Loading API keys" className="flex flex-col gap-6">
      <SettingsSection>
        {/* Placeholder headers stay out of the real <h2> so the loading state
            never exposes an empty heading. */}
        <TitleSkeleton />
        <div className="flex flex-col gap-3 sm:flex-row">
          <Skeleton className="h-10 flex-1" rounded="lg" />
          <Skeleton className="h-10 w-full sm:w-[140px]" rounded="lg" />
        </div>
      </SettingsSection>

      <SettingsSection>
        <div className="flex items-center justify-between gap-4">
          <TitleSkeleton className="w-24" />
          <TitleSkeleton className="w-6" />
        </div>
        <div className="flex flex-col gap-1">
          <KeyListHeader />
          <div className="divide-y divide-seeker-800">
            {[0, 1, 2].map((index) => (
              <KeyRowSkeleton key={index} />
            ))}
          </div>
        </div>
      </SettingsSection>
    </SkeletonGroup>
  );
}

export function ApiKeyManagementPanel({
  publisherScope,
  onPermissionDenied,
}: ApiKeyManagementPanelProps) {
  const trpc = asPortalClient(useTRPC());
  const [keys, setKeys] = useState<DeveloperApiKey[]>([]);
  const [label, setLabel] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [creatingSecret, setCreatingSecret] = useState<string | null>(null);
  const [deletingKey, setDeletingKey] = useState<DeveloperApiKey | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const activeKeys = keys.filter((key) => !key.revokedAt);

  const handleManageTeamPermissionError = useCallback(
    (error: unknown) => {
      if (!isManageTeamPermissionError(error)) {
        return false;
      }

      toast.error('Team management permission required', {
        description: API_KEY_MANAGEMENT_PERMISSION_MESSAGE,
        duration: 6000,
      });
      onPermissionDenied?.();
      return true;
    },
    [onPermissionDenied],
  );

  const loadKeys = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = unwrapPortalResult<unknown>(
        await trpc.developerApiKey.listKeys.query(publisherScope),
      );
      setKeys(readKeys(result));
    } catch (error) {
      if (!handleManageTeamPermissionError(error)) {
        captureAndReportError('Failed to load API keys:', error);
        toast.error('Failed to load API keys');
      }
    } finally {
      setIsLoading(false);
    }
  }, [handleManageTeamPermissionError, publisherScope, trpc]);

  useEffect(() => {
    void loadKeys();
  }, [loadKeys]);

  const handleCreate = useCallback(async () => {
    const trimmedLabel = label.trim();
    if (!trimmedLabel) {
      toast.error('Enter a label for the API key');
      inputRef.current?.focus();
      return;
    }

    setIsCreating(true);
    try {
      const result = unwrapPortalResult<unknown>(
        await trpc.developerApiKey.createKey.mutate({
          publisherId: publisherScope.publisherId,
          publisherType: publisherScope.publisherType,
          label: trimmedLabel,
          permissions: [...DEFAULT_API_KEY_PERMISSIONS],
        }),
      );

      const secret = readSecret(result);
      if (secret) {
        setCreatingSecret(secret);
        try {
          await navigator.clipboard.writeText(secret);
          toast.success('Key created and copied to clipboard');
        } catch {
          toast.success('Key created');
        }
      } else {
        toast.success('Key created');
      }

      setLabel('');
      await loadKeys();
    } catch (error) {
      if (!handleManageTeamPermissionError(error)) {
        captureAndReportError('Failed to create API key:', error);
        toast.error('Failed to create API key');
      }
    } finally {
      setIsCreating(false);
    }
  }, [handleManageTeamPermissionError, label, loadKeys, publisherScope, trpc]);

  const handleDelete = useCallback(
    async (key: DeveloperApiKey) => {
      try {
        unwrapPortalResult<unknown>(
          await trpc.developerApiKey.revokeKey.mutate({ keyId: key.id }),
        );
        toast.success(`"${key.label}" deleted`);
        setDeletingKey(null);
        await loadKeys();
      } catch (error) {
        if (!handleManageTeamPermissionError(error)) {
          captureAndReportError('Failed to delete API key:', error);
          toast.error('Failed to delete API key');
        }
      }
    },
    [handleManageTeamPermissionError, loadKeys, trpc],
  );

  const isListLoading = isLoading && activeKeys.length === 0;

  return (
    <>
      <SettingsSection title="Create a key">
        <div className="flex flex-col gap-3 sm:flex-row">
          <Input
            ref={inputRef}
            wrapperClassName="flex-1"
            aria-label="Key label"
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !isCreating) {
                void handleCreate();
              }
            }}
            placeholder="Key label, e.g. CI pipeline"
          />
          <Button
            variant="primary"
            className="w-full min-w-[140px] sm:w-auto"
            onClick={() => void handleCreate()}
            disabled={isCreating}
            submitting={isCreating}
          >
            <Plus className="size-4" />
            Create Key
          </Button>
        </div>

        {creatingSecret && (
          <SecretBanner
            secret={creatingSecret}
            onDismiss={() => setCreatingSecret(null)}
          />
        )}
      </SettingsSection>

      <SettingsSection
        title="Active keys"
        action={
          isListLoading ? (
            <span className="flex h-5 items-center">
              <Skeleton className="h-3.5 w-6" />
            </span>
          ) : (
            <span className="text-sm text-seeker-400 tabular-nums">
              {activeKeys.length}
            </span>
          )
        }
      >
        <div className="flex flex-col gap-1">
          {(isListLoading || activeKeys.length > 0) && <KeyListHeader />}

          {isListLoading && (
            <div
              role="status"
              aria-label="Loading API keys"
              className={cn(
                'divide-y divide-seeker-800',
                SKELETON_REVEAL_CLASS,
              )}
            >
              {[0, 1, 2].map((index) => (
                <KeyRowSkeleton key={index} />
              ))}
            </div>
          )}

          {activeKeys.length === 0 && !isLoading && (
            <div className="motion-enter flex flex-col items-center gap-1 rounded-lg border border-dashed border-seeker-700 px-4 py-10 text-center">
              <p className="text-sm text-white">No API keys yet</p>
              <p className="text-xs text-seeker-400">
                Create your first key to start using the CLI.
              </p>
            </div>
          )}

          <div className="divide-y divide-seeker-800">
            {activeKeys.map((key) => (
              <KeyRow key={key.id} apiKey={key} onDelete={setDeletingKey} />
            ))}
          </div>
        </div>
      </SettingsSection>

      <Dialog
        open={deletingKey !== null}
        onOpenChange={(open) => {
          if (!open) setDeletingKey(null);
        }}
      >
        <DialogContent className="max-w-md gap-6 rounded-xl border-seeker-800 bg-seeker-900 p-6 [&>button:last-child]:hidden">
          <div className="flex flex-col gap-1">
            <DialogTitle className="text-xl font-semibold leading-7">
              Delete API Key
            </DialogTitle>
            <DialogDescription className="text-sm text-seeker-400">
              <strong className="font-medium text-white">
                {deletingKey?.label}
              </strong>{' '}
              will be permanently revoked. Any integrations using this key will
              stop working immediately.
            </DialogDescription>
          </div>
          <div className="flex flex-col-reverse gap-3 sm:flex-row">
            <Button
              variant="outlineMuted"
              className="w-full sm:w-[120px]"
              onClick={() => setDeletingKey(null)}
            >
              Cancel
            </Button>
            <Button
              variant="dangerOutline"
              className="w-full sm:w-[120px]"
              onClick={() => {
                if (deletingKey) void handleDelete(deletingKey);
              }}
            >
              Delete Key
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
